import type { BubblesMood } from '@/components/tour/MrBubbles';

/**
 * Product-tour steps. The level-1 OVERVIEW targets sidebar section buttons
 * (stable, always visible) via a data-tour attribute, so the whole overview
 * runs on /home with no fragile cross-route waiting. On mobile (sidebar
 * hidden) driver.js falls back to a centered popover.
 */
export interface TourStep {
  element: string;
  title: string;
  description: string;
}

export const TOUR_STEPS: TourStep[] = [
  {
    element: '[data-tour="nav-home"]',
    title: 'Your home base',
    description:
      'This is your dashboard — a quick look at your account, recent orders, and anything that needs attention.',
  },
  {
    element: '[data-tour="nav-orders"]',
    title: 'Place an order',
    description:
      'Open <b>Orders</b> to browse the catalog, add items to your cart, and check out. Your past orders live here too.',
  },
  {
    element: '[data-tour="nav-billing"]',
    title: 'See & pay invoices',
    description:
      'Under <b>Billing</b> you can view invoices, pay them online (one or several at once), see statements, and set up autopay.',
  },
  {
    element: '[data-tour="nav-equipment"]',
    title: 'Cylinders & equipment',
    description:
      'Track your CO₂ / gas cylinder counts and your rented equipment under <b>Equipment</b>.',
  },
  {
    element: '[data-tour="nav-account"]',
    title: 'Your account & payment methods',
    description:
      'Under <b>Account</b> you can manage your profile, invite teammates, and add or change your saved payment methods.',
  },
  {
    element: '[data-tour="nav-service"]',
    title: 'Need a hand?',
    description:
      "Open <b>Service</b> to request help anytime — or use the chat button to reach me. That's the overview! Back on the welcome screen you can take a deeper tutorial for each area.",
  },
];

/**
 * Layered "deep-dive" tutorials — the level-2 tour. After the overview the
 * customer lands back on /welcome and can click into each area for a detailed
 * walkthrough. Each tutorial navigates to its page and points at the specific
 * buttons that matter (via per-step `element` data-tour anchors) with
 * arrow-style spotlights. A step with no `element` falls back to the page's
 * main content region ([data-tour="page-main"]) — used for narration that
 * isn't tied to one button, or for pages we haven't finely anchored yet.
 */
export interface TutorialStep {
  /** data-tour selector to spotlight; omit to highlight the whole page. */
  element?: string;
  title: string;
  description: string;
  /** Mr. Bubbles expression for this step (defaults to 'helpful'). */
  mood?: BubblesMood;
}

export interface SectionTutorial {
  key: string;
  label: string;
  path: string;
  /** One-line teaser shown on the Welcome tile. */
  blurb: string;
  steps: TutorialStep[];
}

export const SECTION_TUTORIALS: SectionTutorial[] = [
  {
    key: 'ordering',
    label: 'Placing an order',
    path: '/order',
    blurb: 'Add your usual items and check out in a few taps.',
    steps: [
      {
        title: 'This is where you order',
        description:
          'Browse the full catalog here. The items you usually buy surface first, so most orders are a few taps — set a quantity and it drops into your cart.',
        mood: 'happy',
      },
      {
        title: 'Review & submit',
        description:
          "When you're ready, open your cart and <b>Review &amp; submit</b>. If your order includes tanks, I'll ask whether you have <b>empty cylinders to return</b> and add the pickup for you.",
        mood: 'helpful',
      },
      {
        title: 'Your order confirmation',
        description:
          "Right after you submit you'll get a <b>Service Fusion order number (SF#)</b> — that's your temporary confirmation. It stays your reference until your product is <b>delivered</b>, then it becomes an invoice.",
        mood: 'excited',
      },
    ],
  },
  {
    key: 'favorites',
    label: 'Your favorites',
    path: '/collection',
    blurb: 'Reorder your regulars without hunting the catalog.',
    steps: [
      {
        title: 'Your favorites & usual items',
        description:
          'Everything you order regularly lives here in <b>My Collection</b> — reorder in a couple of taps without hunting through the catalog.',
        mood: 'happy',
      },
    ],
  },
  {
    key: 'billing',
    label: 'Invoices & billing',
    path: '/invoices',
    blurb: 'Paid vs open, pay all at once, email/print, delivery photos.',
    steps: [
      {
        element: '[data-tour="inv-summary"]',
        title: 'Paid vs. open at a glance',
        description:
          'These cards total your account: <b>open balance</b>, <b>past due</b>, and counts of your <b>open</b> and <b>paid</b> invoices. Tap any card to filter the list below to just those invoices.',
        mood: 'happy',
      },
      {
        element: '[data-tour="inv-filters"]',
        title: 'Filter the list',
        description:
          'Switch between <b>Open</b>, <b>Past due</b>, <b>Paid</b>, and <b>All</b> here — the same slices as the cards above.',
        mood: 'helpful',
      },
      {
        element: '[data-tour="inv-paymultiple"]',
        title: 'Pay them all at once',
        description:
          'Got several open invoices? <b>Pay multiple invoices</b> lets you tick the ones you want and pay them together in one go. You can also pay a single invoice right from its row.',
        mood: 'excited',
      },
      {
        element: '[data-tour="inv-tools"]',
        title: 'Statements & autopay',
        description:
          'View your full account <b>statement</b> here, or set up <b>Autopay</b> so rentals pay themselves automatically.',
        mood: 'helpful',
      },
      {
        element: '[data-tour="inv-list"]',
        title: 'Open any invoice',
        description:
          'Click any invoice to open it. Inside you can <b>Download</b> or <b>Print</b> it, email a copy, and — once an order has been delivered — tap the <b>Delivery confirmation</b> to see the photos and signature from the drop-off.',
        mood: 'thinking',
      },
    ],
  },
  {
    key: 'payment-methods',
    label: 'Payment methods',
    path: '/settings/payments',
    blurb: 'Save cards and pick which one covers what.',
    steps: [
      {
        title: 'Your saved cards',
        description:
          'Add or change your cards here — and choose which card covers what. You can put <b>rentals on one card and product orders on another</b>, and change it anytime.',
        mood: 'helpful',
      },
    ],
  },
  {
    key: 'comms',
    label: 'Emails & paper mail',
    path: '/settings/notifications',
    blurb: 'Choose who gets emails, turn on paper invoices, route per person.',
    steps: [
      {
        element: '[data-tour="notif-email"]',
        title: 'Choose your emails',
        description:
          'Turn on the emails you want — <b>monthly statements</b> and <b>overdue reminders</b>. Invoice emails are on by default so you always get a copy when one is issued.',
        mood: 'happy',
      },
      {
        element: '[data-tour="notif-paper"]',
        title: 'Want paper in the mail?',
        description:
          'Prefer physical copies? Flip on <b>paper statements</b> or <b>paper invoice copies</b> and we\'ll mail printed copies to your billing address — separate from the emails above.',
        mood: 'helpful',
      },
      {
        element: '[data-tour="notif-addresses"]',
        title: 'Different emails for different people',
        description:
          'Add up to four billing contacts — <b>Primary</b>, <b>Secondary</b>, <b>Optional</b>, and <b>Accounting</b>. Put your bookkeeper, your manager, and yourself on separate addresses.',
        mood: 'thinking',
      },
      {
        element: '[data-tour="notif-routing"]',
        title: 'Route who gets what',
        description:
          'Then decide what each address receives — invoices, statements, reminders — by tapping the chips. Your accountant can get invoices only; you can get everything.',
        mood: 'excited',
      },
    ],
  },
  {
    key: 'cylinders',
    label: 'CO₂ cylinders',
    path: '/cylinders',
    blurb: 'Live tank count and every in & out.',
    steps: [
      {
        title: 'Your cylinders, anytime',
        description:
          'If you rent CO₂ / gas cylinders, this page shows your <b>live count and every in &amp; out</b> — deliveries, pickups, and audit adjustments — whenever you want to check.',
        mood: 'happy',
      },
    ],
  },
  {
    key: 'equipment',
    label: 'Equipment rentals',
    path: '/equipment',
    blurb: 'Review the equipment you have on rent.',
    steps: [
      {
        title: 'Your rented equipment',
        description:
          'If you have equipment on rent from us, this is where you review what you have and its details.',
        mood: 'helpful',
      },
    ],
  },
  {
    key: 'users',
    label: 'Adding users',
    path: '/settings/team',
    blurb: 'Invite your team — each with their own sign-in.',
    steps: [
      {
        title: 'Add your team',
        description:
          'Invite the people at your business here and set what each can do (place orders, pay bills, etc.). They get their own sign-in — no sharing passwords.',
        mood: 'excited',
      },
    ],
  },
  {
    key: 'service',
    label: 'Getting help',
    path: '/service',
    blurb: 'Request service, or just ask me anytime.',
    steps: [
      {
        title: 'Service & help',
        description:
          "Request service here anytime. And I'm <b>Mr. Bubbles</b> — the chat button in the corner. Ask me about your account, orders, or how anything works, on any page.",
        mood: 'happy',
      },
    ],
  },
];
